import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { nameSchema } from "@/lib/name-validation";


// ============= Public: request access with a code =============
const requestSchema = z.object({
  email: z.string().trim().email().max(255),
  firstName: nameSchema("First name"),
  lastName: nameSchema("Last name"),
  code: z.string().trim().min(1).max(32),
  turnstileToken: z.string().min(1).max(2048),
});

const accessibleLeagueSchema = z.object({
  slug: z.string().trim().min(1).max(100),
});

export const getAccessibleLeagueBySlug = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => accessibleLeagueSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { userId } = context;

    const [{ data: league }, { data: adminRole }] = await Promise.all([
      supabaseAdmin
        .from("leagues")
        .select("id, name, slug, bracket_lock_at, bracket_lock_timezone, tournament_type, group_stage_lock_at, max_entries_per_member")
        .eq("slug", data.slug)
        .maybeSingle(),
      supabaseAdmin
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .eq("role", "admin")
        .maybeSingle(),
    ]);

    if (!league) {
      throw new Error("League not found");
    }

    // Knockout is "pickable" once admin has seeded at least one R32 match
    // with both teams. Used by leaderboard/entry CTAs to decide whether to
    // send users to Groups or Knockout in Group+Knockout leagues.
    let knockoutPickable = false;
    {
      const { data: r32 } = await supabaseAdmin
        .from("tournament_matches")
        .select("home_team_id, away_team_id")
        .eq("tournament_type", league.tournament_type)
        .eq("round", "r32")
        .not("home_team_id", "is", null)
        .not("away_team_id", "is", null)
        .limit(1);
      knockoutPickable = (r32 ?? []).length > 0;
    }

    // Look up this user's membership row (if any) to know their role.
    // Admins without membership default to 'participant' so they can browse.
    const { data: membership } = await supabaseAdmin
      .from("league_memberships")
      .select("status, member_role")
      .eq("league_id", league.id)
      .eq("user_id", userId)
      .maybeSingle();

    if (!adminRole) {
      if (!membership || membership.status !== "approved") {
        throw new Error("You don't have access to this league");
      }
    }

    const memberRole: "participant" | "watcher" =
      membership?.member_role === "watcher" ? "watcher" : "participant";

    return { ...league, member_role: memberRole, knockout_pickable: knockoutPickable };
  });

export const requestLeagueAccess = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => requestSchema.parse(input))
  .handler(async ({ data }) => {
    const [{ getRequestIP }, { supabaseAdmin }, { verifyTurnstileToken }, { logAdminAction }, { getPublicOrigin }, { findAuthUserByEmail }] =
      await Promise.all([
        import("@tanstack/react-start/server"),
        import("@/integrations/supabase/client.server"),
        import("@/lib/turnstile"),
        import("@/lib/server/audit.server"),
        import("@/lib/server/origin"),
        import("@/lib/server/auth-users.server"),
      ]);
    // Verify CAPTCHA before any DB work to mitigate bot abuse.
    const remoteIp = (() => {
      try {
        return getRequestIP({ xForwardedFor: true }) ?? null;
      } catch {
        return null;
      }
    })();
    await verifyTurnstileToken(data.turnstileToken, remoteIp);

    // Look up league by code (case-insensitive). Always return generic success
    // to avoid letting attackers probe valid codes.
    const { data: league } = await supabaseAdmin
      .from("leagues")
      .select("id, slug, name, auto_approve_join_by_code")
      .ilike("code", data.code)
      .maybeSingle();

    if (!league) {
      // Pretend success — silent rejection
      return { ok: true };
    }

    const autoApprove = !!league.auto_approve_join_by_code;

    // If a user already exists with this email, check their membership status
    const existing = await findAuthUserByEmail(data.email);

    if (existing) {
      // Direct membership request (skip pending_signups since they have an account)
      const { data: existingMem } = await supabaseAdmin
        .from("league_memberships")
        .select("id, status")
        .eq("league_id", league.id)
        .eq("user_id", existing.id)
        .maybeSingle();

      if (!existingMem) {
        await supabaseAdmin.from("league_memberships").insert({
          league_id: league.id,
          user_id: existing.id,
          status: autoApprove ? "approved" : "pending",
          decided_at: autoApprove ? new Date().toISOString() : null,
        });
      } else if (autoApprove && existingMem.status !== "approved") {
        // Auto-approve flips pending or denied → approved.
        await supabaseAdmin
          .from("league_memberships")
          .update({
            status: "approved",
            decided_at: new Date().toISOString(),
            decided_by: null,
            denial_reason: null,
          })
          .eq("id", existingMem.id);
      }
      return { ok: true };
    }

    // No user yet.
    if (autoApprove) {
      // Auto-approve, no account: create the auth user immediately
      // (email auto-confirmed + random stub password) and email them a
      // 6-digit activation code. They'll set their real password via
      // /activate-account.
      const stubPassword = crypto.randomUUID() + crypto.randomUUID();
      const { data: created, error: createErr } =
        await supabaseAdmin.auth.admin.createUser({
          email: data.email,
          password: stubPassword,
          email_confirm: true,
          user_metadata: {
            first_name: data.firstName,
            last_name: data.lastName,
          },
        });

      let newUserId: string | null = created?.user?.id ?? null;
      if (!newUserId && createErr) {
        // Race: maybe a parallel request just created them.
        const found = await findAuthUserByEmail(data.email);
        newUserId = found?.id ?? null;
      }

      if (!newUserId) {
        // Could not provision — fall back to the pending_signups path so the
        // request is not silently lost.
        const { data: existingPending } = await supabaseAdmin
          .from("pending_signups")
          .select("id")
          .ilike("email", data.email)
          .eq("league_id", league.id)
          .eq("status", "pending")
          .maybeSingle();
        if (!existingPending) {
          await supabaseAdmin.from("pending_signups").insert({
            email: data.email,
            first_name: data.firstName,
            last_name: data.lastName,
            league_id: league.id,
            status: "pending",
          });
        }
        return { ok: true };
      }

      // Ensure profile names are populated.
      await supabaseAdmin
        .from("profiles")
        .update({ first_name: data.firstName, last_name: data.lastName })
        .eq("id", newUserId);

      // Approved membership for this league.
      await supabaseAdmin.from("league_memberships").insert({
        league_id: league.id,
        user_id: newUserId,
        status: "approved",
        decided_at: new Date().toISOString(),
      });

      // Send activation code email (only if this was a brand new createUser).
      if (created?.user?.id) {
        const { sendActivationCodeEmail } = await import(
          "@/lib/server/send-activation-email.server"
        );
        await sendActivationCodeEmail({
          userId: newUserId,
          email: data.email,
          firstName: data.firstName,
        });
      }

      await logAdminAction({
        action: "membership_approved",
        actorUserId: newUserId,
        targetType: "user",
        targetId: newUserId,
        targetLabelSnapshot: data.email,
        reason: null,
        metadata: {
          league_id: league.id,
          league_name: league.name,
          via: "auto_approve_request_access",
        },
      });

      return { ok: true };
    }

    // Default flow: create a pending signup, await admin review.
    // Avoid duplicates of pending requests for the same email+league
    const { data: existingPending } = await supabaseAdmin
      .from("pending_signups")
      .select("id")
      .ilike("email", data.email)
      .eq("league_id", league.id)
      .eq("status", "pending")
      .maybeSingle();

    if (!existingPending) {
      await supabaseAdmin.from("pending_signups").insert({
        email: data.email,
        first_name: data.firstName,
        last_name: data.lastName,
        league_id: league.id,
        status: "pending",
      });
    }

    return { ok: true };
  });


// ============= Authenticated: join another league with a code =============
const joinSchema = z.object({
  code: z.string().trim().min(1).max(32),
});

export const joinAnotherLeague = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => joinSchema.parse(input))
  .handler(async ({ data, context }) => {
    const [{ supabaseAdmin }, { logAdminAction }] = await Promise.all([
      import("@/integrations/supabase/client.server"),
      import("@/lib/server/audit.server"),
    ]);
    const { userId } = context;

    const { data: league } = await supabaseAdmin
      .from("leagues")
      .select("id, name, slug, auto_approve_join_by_code")
      .ilike("code", data.code)
      .maybeSingle();

    if (!league) {
      throw new Error("Invalid league code");
    }

    const autoApprove = !!league.auto_approve_join_by_code;
    const nowIso = new Date().toISOString();

    // Check existing membership
    const { data: existing } = await supabaseAdmin
      .from("league_memberships")
      .select("id, status, attempt_count")
      .eq("league_id", league.id)
      .eq("user_id", userId)
      .maybeSingle();

    if (existing) {
      if (existing.status === "approved") {
        return { ok: true, alreadyMember: true, leagueName: league.name, leagueSlug: league.slug };
      }
      if (existing.status === "pending") {
        if (autoApprove) {
          await supabaseAdmin
            .from("league_memberships")
            .update({ status: "approved", decided_at: nowIso, decided_by: null })
            .eq("id", existing.id);
          return { ok: true, autoApproved: true, leagueName: league.name, leagueSlug: league.slug };
        }
        return { ok: true, alreadyPending: true, leagueName: league.name, leagueSlug: league.slug };
      }
      // denied → either auto-approve or re-open as pending.
      if (autoApprove) {
        await supabaseAdmin
          .from("league_memberships")
          .update({
            status: "approved",
            requested_at: nowIso,
            decided_at: nowIso,
            decided_by: null,
            denial_reason: null,
          })
          .eq("id", existing.id);
        return { ok: true, autoApproved: true, leagueName: league.name, leagueSlug: league.slug };
      }
      const nextAttempt = (existing.attempt_count ?? 1) + 1;
      await supabaseAdmin
        .from("league_memberships")
        .update({
          status: "pending",
          requested_at: nowIso,
          decided_at: null,
          decided_by: null,
          denial_reason: null,
          attempt_count: nextAttempt,
        })
        .eq("id", existing.id);
      await logAdminAction({
        action: "membership_request_resubmitted",
        actorUserId: userId,
        targetType: "user",
        targetId: userId,
        targetLabelSnapshot: null,
        reason: null,
        metadata: {
          league_id: league.id,
          league_name: league.name,
          attempt_count: nextAttempt,
          via: "join_another_league",
        },
      });
      return { ok: true, leagueName: league.name, leagueSlug: league.slug, resubmitted: true, attemptCount: nextAttempt };
    }

    await supabaseAdmin.from("league_memberships").insert({
      league_id: league.id,
      user_id: userId,
      status: autoApprove ? "approved" : "pending",
      decided_at: autoApprove ? nowIso : null,
    });
    await logAdminAction({
      action: autoApprove ? "membership_approved" : "membership_requested",
      actorUserId: userId,
      targetType: "user",
      targetId: userId,
      targetLabelSnapshot: null,
      reason: null,
      metadata: {
        league_id: league.id,
        league_name: league.name,
        via: autoApprove ? "auto_approve_join_another_league" : "join_another_league",
      },
    });

    return { ok: true, autoApproved: autoApprove, leagueName: league.name, leagueSlug: league.slug };
  });


// ============= Authenticated: list current user's pending + denied requests =============
export type MyLeagueRequest = {
  membershipId: string;
  leagueId: string;
  leagueName: string;
  leagueSlug: string;
  status: "pending" | "denied";
  requestedAt: string;
  decidedAt: string | null;
  denialReason: string | null;
  attemptCount: number;
};

export const getMyLeagueRequests = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<MyLeagueRequest[]> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { userId } = context;
    const { data, error } = await supabaseAdmin
      .from("league_memberships")
      .select(
        "id, status, requested_at, decided_at, denial_reason, attempt_count, league_id, leagues:league_id ( name, slug, is_hidden )",
      )
      .eq("user_id", userId)
      .in("status", ["pending", "denied"])
      .order("requested_at", { ascending: false });

    if (error) throw new Error(error.message);

    return (data ?? [])
      .filter((m: (typeof data)[number]) => m.leagues && !m.leagues.is_hidden)
      .map((m: (typeof data)[number]) => ({
        membershipId: m.id,
        leagueId: m.league_id,
        leagueName: m.leagues!.name,
        leagueSlug: m.leagues!.slug,
        status: m.status as "pending" | "denied",
        requestedAt: m.requested_at,
        decidedAt: m.decided_at,
        denialReason: m.denial_reason,
        attemptCount: m.attempt_count ?? 1,
      }));
  });

// ============= Authenticated: cancel my own pending request =============
const membershipIdSchema = z.object({
  membershipId: z.string().uuid(),
});

export const cancelMyLeagueRequest = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => membershipIdSchema.parse(input))
  .handler(async ({ data, context }) => {
    const [{ supabaseAdmin }, { logAdminAction }] = await Promise.all([
      import("@/integrations/supabase/client.server"),
      import("@/lib/server/audit.server"),
    ]);
    const { userId } = context;

    // Snapshot for audit before delete; only allow the owner to cancel
    // a pending or denied row of their own.
    const { data: row } = await supabaseAdmin
      .from("league_memberships")
      .select("id, user_id, status, league_id, leagues:league_id ( name )")
      .eq("id", data.membershipId)
      .maybeSingle();

    if (!row) throw new Error("Request not found");
    if (row.user_id !== userId) throw new Error("Not allowed");
    if (row.status === "approved") {
      throw new Error("You're already a member — leave from the league page instead.");
    }

    const { error } = await supabaseAdmin
      .from("league_memberships")
      .delete()
      .eq("id", data.membershipId);
    if (error) throw new Error(error.message);

    await logAdminAction({
      action:
        row.status === "denied"
          ? "membership_request_dismissed"
          : "membership_request_cancelled",
      actorUserId: userId,
      targetType: "user",
      targetId: userId,
      targetLabelSnapshot: null,
      reason: null,
      metadata: {
        league_id: row.league_id,
        league_name: row.leagues?.name ?? null,
        prior_status: row.status,
      },
    });

    return { ok: true };
  });

// ============= Authenticated: re-submit a denied request =============
export const resubmitMyLeagueRequest = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => membershipIdSchema.parse(input))
  .handler(async ({ data, context }) => {
    const [{ supabaseAdmin }, { logAdminAction }] = await Promise.all([
      import("@/integrations/supabase/client.server"),
      import("@/lib/server/audit.server"),
    ]);
    const { userId } = context;

    const { data: row } = await supabaseAdmin
      .from("league_memberships")
      .select("id, user_id, status, attempt_count, league_id, leagues:league_id ( name )")
      .eq("id", data.membershipId)
      .maybeSingle();

    if (!row) throw new Error("Request not found");
    if (row.user_id !== userId) throw new Error("Not allowed");
    if (row.status !== "denied") {
      throw new Error("Only denied requests can be re-submitted.");
    }

    const nextAttempt = (row.attempt_count ?? 1) + 1;
    const { error } = await supabaseAdmin
      .from("league_memberships")
      .update({
        status: "pending",
        requested_at: new Date().toISOString(),
        decided_at: null,
        decided_by: null,
        denial_reason: null,
        attempt_count: nextAttempt,
      })
      .eq("id", data.membershipId);
    if (error) throw new Error(error.message);

    await logAdminAction({
      action: "membership_request_resubmitted",
      actorUserId: userId,
      targetType: "user",
      targetId: userId,
      targetLabelSnapshot: null,
      reason: null,
      metadata: {
        league_id: row.league_id,
        league_name: row.leagues?.name ?? null,
        attempt_count: nextAttempt,
        via: "resubmit_button",
      },
    });

    return { ok: true, attemptCount: nextAttempt };
  });
